/* eslint-disable */
import axios from 'axios';
import 'vue-loading-overlay/dist/css/index.css'; // Keep styles but don't use the library
import Cookies from 'js-cookie';
import getBrowserFingerprint from 'get-browser-fingerprint';
import router from '../router';
import { clearAllAuthData } from './auth-utils';
import store from '../store';

// Simple request counter for multiple concurrent requests
class SimpleLoader {
  private activeRequests: number = 0;
  private loader: HTMLElement | null = null;

  private createLoader(): void {
    if (!this.loader) {
      this.loader = document.createElement('div');
      this.loader.id = 'api-request-loader';
      this.loader.style.position = 'fixed';
      this.loader.style.top = '0';
      this.loader.style.left = '0';
      this.loader.style.width = '0';
      this.loader.style.height = '2px';
      this.loader.style.backgroundColor = '#D0A441';
      this.loader.style.zIndex = '9999';
      this.loader.style.transition = 'width 0.3s ease-in-out';
      document.body.appendChild(this.loader);

      // Animate to 30% immediately
      setTimeout(() => {
        if (this.loader) this.loader.style.width = '30%';
      }, 10);

      // Animate to 60% after a short delay
      setTimeout(() => {
        if (this.loader) this.loader.style.width = '60%';
      }, 100);
    }
  }

  show(requestId: string): void {
    try {
      this.activeRequests++;

      // Create loader if it doesn't exist
      if (this.activeRequests === 1) {
        this.createLoader();
      }
    } catch (e) {
      console.error('Error showing request loader:', e);
    }
  }

  hide(requestId: string): void {
    try {
      this.activeRequests = Math.max(0, this.activeRequests - 1);

      // Remove loader if no active requests
      if (this.activeRequests === 0 && this.loader) {
        // Animate to 100% then remove
        this.loader.style.width = '100%';

        setTimeout(() => {
          if (this.loader && this.loader.parentNode) {
            this.loader.parentNode.removeChild(this.loader);
            this.loader = null;
          }
        }, 200);
      }
    } catch (e) {
      console.error('Error hiding request loader:', e);
    }
  }
}

const simpleLoader = new SimpleLoader();

const setDateTimes = (el : any) => {
  if(el.created_at){
    el.created_at_time = (new Date(el.created_at)).getTime()
  }

  if(el.updated_at){
    el.updated_at_time = (new Date(el.updated_at)).getTime()
  }

  return el
}

const sortFn = (a : any, b : any) => {
  return b.created_at_time - a.created_at_time
}

export interface APIInterface {
    setAuthentication(auth : any, url : any) : void
    post(url : string, data : any) : Promise<any>
    put(url : string, data : any) : Promise<any>
    get(url : string, config?: any) : Promise<any>
    delete(url : string) : Promise<any>
    uploadDocument(file : File) : Promise<any>
}

export interface ImportColumn {
  name : string
  comArray? : boolean
  numType? : boolean
  boolType? : boolean
  column : string
  required? : boolean
  dateType? : boolean
  // Which column to use from
  // excel.
  targetColumn? : number
}

export class APIClient {

	public instance : any
	private basePath : string = ""


	constructor(auth : any = {}, url : any = process.env.VUE_APP_API_URL){
		this.setAuthentication(auth, url)
        console.log(this.basePath, url)
	}

	public setAuthentication(auth : any, url : any) : void {

		if (url){
			this.basePath = url
		}

        // Not sending roles or branch in headers - JWT token will contain all necessary authorization info

        // Try to get token from cookies first
        let token = Cookies.get("API_TOKEN");
        let tokenSource = "cookie";

        // If no cookie token, try localStorage
        if (!token) {
            token = window.localStorage.getItem("user_token");
            tokenSource = "localStorage";
        }

        // If we found a token in either location
        if (token) {
            // Ensure token has Bearer prefix
            const tokenWithBearer = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

            // Set token in multiple header formats to ensure compatibility
            auth["X-TOKEN"] = tokenWithBearer;
            auth["Authorization"] = tokenWithBearer;

            // Fix inconsistent casing that might cause CORS issues
            auth["x-token"] = tokenWithBearer;
            auth["authorization"] = tokenWithBearer;

            console.log(`Token from ${tokenSource} set in headers: ${tokenWithBearer.substring(0, 15)}...`);

            // If token was found in localStorage but not in cookie, restore it to cookie
            // This helps with browser refreshes and cross-tab consistency
            if (tokenSource === "localStorage" && !Cookies.get("API_TOKEN")) {
                console.log("Restoring missing cookie from localStorage token");
                try {
                    Cookies.set("API_TOKEN", token, {
                        path: '/',
                        sameSite: 'strict',
                        secure: window.location.protocol === 'https:'
                    });
                } catch (e) {
                    console.error("Failed to restore API_TOKEN cookie:", e);
                }
            }
        } else {
            console.warn("No API_TOKEN cookie or localStorage token found!");

            // Check for Login URL to avoid unnecessary warnings during login process
            if (!window.location.pathname.includes('/login')) {
                console.error("Authentication required but no token found. Redirecting to login soon.");

                // Skip automatic redirects - let the router handle authentication
                // This prevents premature redirects during initialization
                // The auth check will happen naturally when navigating to protected routes
            }
        }

        // We no longer need to set user ID in headers as the JWT token contains identity
        // This avoids unnecessary CORS preflight requests and follows best security practices

        // Just for debug purposes during development, log the user info
        if (process.env.NODE_ENV === 'development') {
            try {
                if (window.localStorage["userId"]) {
                    console.log("Development-only: User ID from localStorage:", window.localStorage["userId"]);
                } else if (window.localStorage["user"]) {
                    const userData = JSON.parse(window.localStorage["user"]);
                    if (userData._id) {
                        console.log("Development-only: User ID from user object:", userData._id);
                    }
                }
            } catch (e) {
                console.warn("Error accessing user data for development logging:", e);
            }
        }

        const fg = getBrowserFingerprint({
            enableWebgl: true,
        })
        auth.device = fg

        // No longer sending username in headers - authentication is handled by JWT token

		this.instance = axios.create({
			baseURL: this.basePath,
			timeout: 160000,
			headers: auth
		});



    this.instance.interceptors.request.use(function (config : any) {
      // Do something before request is sent
      // Ensure both headers are populated with the latest token
      // Prefer Vuex token, then cookie, then localStorage
      const storedToken = store.state.token || Cookies.get("API_TOKEN") || window.localStorage.getItem("user_token");
      if (storedToken) {
          const bearerToken = storedToken.startsWith("Bearer ") ? storedToken : `Bearer ${storedToken}`;
          config.headers['Authorization'] = bearerToken;
          config.headers['X-TOKEN'] = bearerToken;
      }

      // Ensure Authorization and X-TOKEN headers both have Bearer prefix
      if (config.headers['Authorization'] && !config.headers['Authorization'].startsWith('Bearer ')) {
        config.headers['Authorization'] = `Bearer ${config.headers['Authorization']}`;
      }

      if (config.headers['X-TOKEN'] && !config.headers['X-TOKEN'].startsWith('Bearer ')) {
        config.headers['X-TOKEN'] = `Bearer ${config.headers['X-TOKEN']}`;
      }

      // Debug log for authentication headers on protected endpoints
      if (process.env.NODE_ENV === 'development' && (config.url?.includes("/me") || config.url?.includes("/api/v1/"))) {
        console.log("Request to authenticated endpoint:", config.url);
        console.log("Auth headers:", {
          Authorization: config.headers['Authorization'] ?
                        (config.headers['Authorization'].length > 15 ?
                         config.headers['Authorization'].substring(0, 15) + "..." :
                         config.headers['Authorization']) :
                        "not set",
          "X-TOKEN": config.headers['X-TOKEN'] ?
                    (config.headers['X-TOKEN'].length > 15 ?
                     config.headers['X-TOKEN'].substring(0, 15) + "..." :
                     config.headers['X-TOKEN']) :
                    "not set"
        });
      }

      // Remove any unnecessary identity/authorization headers (best practice)
      if (config.headers) {
        // Remove all variations of user ID headers
        delete config.headers['x-user-id'];
        delete config.headers['X-User-ID'];
        delete config.headers['userId'];
        delete config.headers['user-id'];
        delete config.headers['UserId'];

        // Remove any username headers
        delete config.headers['username'];
        delete config.headers['Username'];
        delete config.headers['user_name'];
        delete config.headers['UserName'];
        delete config.headers['x-username'];
        delete config.headers['X-Username'];

        // Remove role and branch headers - all auth info should be in the JWT token
        delete config.headers['roles'];
        delete config.headers['Roles'];
        delete config.headers['role'];
        delete config.headers['Role'];
        delete config.headers['branch'];
        delete config.headers['Branch'];
        delete config.headers['branch_id'];
        delete config.headers['branchId'];
        delete config.headers['BranchId'];
      }

      // Only show loading indicators for non-background requests
      if (!config.url?.includes("/messages/") &&
          !config.url?.includes("/inbox") &&
          !config.url?.includes("/me/tasks")) {

        // Generate a unique request ID for this request
        const requestId = Date.now().toString() + Math.random().toString(36).substring(2, 9);
        config.requestId = requestId;

        // Show loader for this request
        simpleLoader.show(requestId);
      }

      return config;
    }, function (error : any) {
      // Do something with request error
      return Promise.reject(error);
    });

    this.instance.interceptors.response.use(function (response : any) {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data

      // Hide the loader for this request
      if (response.config.requestId) {
        simpleLoader.hide(response.config.requestId);
      }

      // Handle master data responses which will be in the format { "[Type]s": [...] }
      // First check if the response is from a master data endpoint
      const url = response.config.url;
      if (url && typeof url === 'string' && url.includes('/masters/')) {
        // Get the master type from URL: /api/v1/masters/[type] → [type]
        const urlParts = url.split('/');
        const masterType = urlParts[urlParts.length - 1];

        // If we have a pluralized key matching the master type (e.g., "customerTypes")
        const masterTypePlural = masterType + 's';

        // If the data is already in the expected format { "[Type]s": [...] }
        // then just set it as master_data for backward compatibility
        if (response.data && response.data[masterTypePlural]) {
          response.data.master_data = response.data[masterTypePlural];
          response.data.type = masterType;
        }
      }

      // Process date timestamps for sorting
      if(Array.isArray(response.data)){
        response.data = response.data.map(setDateTimes)
        .sort(sortFn)
      }

      if(Array.isArray(response.data?.results)){
        response.data.results = response.data.results.map(setDateTimes)
        .sort(sortFn)
      }

      // Also handle master_data if it exists
      if(Array.isArray(response.data?.master_data)){
        response.data.master_data = response.data.master_data.map(setDateTimes);
      }

      return response;
    }, function (error : any) {
      // Hide the loader for error responses
      if (error.config?.requestId) {
        simpleLoader.hide(error.config.requestId);
      }

      // Handle authentication errors (401 Unauthorized)
      if (error.response && error.response.status === 401) {
        // Check if we're on the login page or getting a first-time 401
        // If we're on the login page, don't redirect (would cause a loop)
        const isOnLoginPage = window.location.pathname.includes('/login');
        const isResetPassword = window.location.pathname.includes('/reset-password');
        const isForgotPassword = window.location.pathname.includes('/forgot-password');

        // Check if we're in the login flow or initial app loading
        // We want to be more tolerant of auth failures during these periods
        const isJustLoggedIn = document.cookie.includes('just_logged_in');
        const isMeEndpoint = error.config?.url?.includes('/me');
        const isInitialLoading = document.readyState !== 'complete' ||
                                 performance.now() < 5000; // First 5 seconds of page load

        // If we just logged in, temporarily mark this state to prevent logout loops
        if (!isJustLoggedIn && error.config?.url?.includes('/login')) {
          try {
            Cookies.set('just_logged_in', 'true', {
              expires: new Date(new Date().getTime() + 30000),  // 30 seconds
              path: '/'
            });
            console.log('Set temporary login state cookie to prevent logout loops');
          } catch (e) {
            console.warn('Failed to set login state cookie:', e);
          }
        }

        // If we get a 401 during these scenarios, just log it but don't redirect
        if (isMeEndpoint || isJustLoggedIn || isInitialLoading ||
            isOnLoginPage || isResetPassword || isForgotPassword) {
          // Be more tolerant during initialization and authentication flows
          console.warn("Authentication check failed with 401 - continuing without redirect");
          console.warn("Context: Just logged in: " + isJustLoggedIn +
                      ", Is /me endpoint: " + isMeEndpoint +
                      ", Initial loading: " + isInitialLoading);
          // Allow the application to continue - tokens might still be initializing
          return Promise.reject(error);
        }

        // For 401 errors on important API endpoints that aren't initialization-related,
        // show a confirmation prompt before redirecting
        if (!error.config?.url?.includes('/dashboard') &&
            !error.config?.url?.includes('/masters')) {

          console.warn("Authentication error (401) on important request. Confirming with user.");

          // Ask user before redirecting (only if this is a user-initiated action)
          if (confirm("Your session appears to have expired. Would you like to log in again?")) {
            console.log("User confirmed redirection to login page");

            // Remember current page for redirect back after login
            try {
              sessionStorage.setItem('login_redirect', window.location.pathname);
            } catch (e) {
              console.warn("Could not save redirect location:", e);
            }

            // Clear only auth data, not entire localStorage
            localStorage.removeItem('user_token');
            localStorage.removeItem('user');
            localStorage.removeItem('userId');
            Cookies.remove('API_TOKEN', { path: '/' });

            // Redirect with minimal parameters
            window.location.href = "/login?expired=true&t=" + Date.now();

            // Return a dummy promise to prevent further error handling
            return new Promise(() => {});
          } else {
            console.log("User declined redirection, staying on current page");
            // User declined, continue using the app in a potentially limited state
            return Promise.reject(error);
          }
        }

        // For non-critical endpoints, just log the error without redirecting
        console.warn("Non-critical 401 error, continuing without redirect");
        return Promise.reject(error);
      }

      // Handle 404 errors more gracefully
      // This is useful for dashboard APIs that might not be implemented yet
      if (error.response && error.response.status === 404) {
        const url = error.config?.url || '';

        // Special handling for dashboard/reporting URLs
        if (url.includes('/dashboard') || url.includes('/rsm/') || url.includes('/dse/') || url.includes('/manager/')) {
          console.warn(`Resource not found (404): ${url} - Using empty data response`);

          // Return an empty successful response with appropriate structure
          // This prevents errors in components that might not handle 404s well
          return Promise.resolve({
            status: 200,
            data: url.includes('/dashboard') ? [] : {},
            config: error.config,
            headers: {},
            statusText: 'OK (Empty Mock)',
          });
        }

        // For style.css 404 errors, suppress the error completely
        if (url.includes('style.css')) {
          console.warn('style.css resource not found (404) - suppressing error');
          return Promise.resolve({
            status: 200,
            data: {},
            config: error.config,
            headers: {},
            statusText: 'OK (Empty Mock)',
          });
        }
      }

      // If no special handling was applied, reject with the original error
      return Promise.reject(error);
    });

	}

  public async uploadDocument(file : File, metadata : any = {}){
      var formData = new FormData();
      formData.append("document", file);
      

      return this.instance.post("/documents/upload", formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
      })
  }

	public async post(url : string, data : any) : Promise<any> {
		return this.instance.post(url, data)
	}

    public async put(url : string, data : any) : Promise<any> {
		return this.instance.put(url, data)
	}

	public async get(url : string, config?: any) : Promise<any> {
		return this.instance.get(url, config)
	}

	public async delete(url : string) : Promise<any> {
		return this.instance.delete(url)
	}

}

export function getMapFileData(docList : any[]){
  if(!docList)
    return []

  let result : any[] = []

  for(let group of docList){

    const docType = group.document_type
    group.documents.forEach((el : any) => {
      if(!el.file)
        return

	  if(!el.document_id && docType == "adhoc")
		return alert("Adhoc Document is missing ID")

	  if(el.document_id){
		el.id = el.document_id
		el.issued_at = el.date_of_issue
    	el.expire_at = el.date_of_expiry
	  }

      if(!el.id){

        for(let f of el.file){

          let fparts  = f.name.split(".")
          let ext = fparts[fparts.length - 1]

          result.push({
            parent_type : docType,
            type : el.document,
            id : f.name,
            extension: ext,
            issued_at : el.issued_at,
            expire_at : el.expire_at,
            file : f,
            required_now: el.required_now
          })

        }

        return
      }

      let f1 = el.file[0] ? el.file[0] : el.file
      let fparts  = f1.name.split(".")
      let ext = fparts[fparts.length - 1]

      result.push({
        parent_type : docType,
        type : el.document,
        id : el.id,
        extension: ext,
        issued_at : el.issued_at,
        expire_at : el.expire_at,
        file : f1,
        required_now: el.required_now
      })

    })

  }

  return result

}


export function shouldCollectDocument(
	stage : string,
	document_group : string,
	stages : any[]
) : boolean {

	const s = stages.filter(
		(el: any) => el.loan_stage == stage
	)[0] as any

	if(!s)
		return false

	return s.document_group?.indexOf(document_group) !== -1
}

export function validateFiles(docs : any){

  	let result = true

	docs.forEach((group : any) => {

	  const missingFilled = group.documents.filter(
	    (e : any) => !e.file && e.mandatory
	  ).length

 	  if(missingFilled && group.required_now){
 	  	alert("Missing documents for " + group.document_type )
	    result = false
 	  }

	})

  return result
}



export function scrollIntoError(position : boolean = true){
  setTimeout(() => {
    const els = document.getElementsByClassName("p-invalid")
    for(let e of els){
      e.scrollIntoView(position)
    }
  }, 500)
}
